import {AssetManagerData} from '@/api-requests/asset-managers';
import {ChartData, Position, PositionBalance} from './OverviewProvider';
import {useMemo} from 'react';
import {formatUnits} from 'ethers';
import {addPositionChartData} from '@/providers/overview/addPositionChartData';
import {useAccountData} from '@/providers/account/AccountProvider';
import {AccountAssetManagerData} from './useAssetManagerData';
import {compareAddresses} from '@kei.fi/data';

export const useAssetManagerPositions = (
  chartData: ChartData,
  assetManagers: AssetManagerData[] = [],
  assetManagerData?: AccountAssetManagerData
) => {
  const accountData = useAccountData();

  return useMemo((): Position[] => {
    return parseAssetManagerData(
      chartData,
      assetManagers,
      accountData,
      assetManagerData
    );
  }, [assetManagers, chartData, accountData.blockNumber, assetManagerData]);
};

function parseAssetManagerData(
  chartData: ChartData,
  assetManagers: AssetManagerData[],
  accountData: any,
  accountAssetManagerData?: AccountAssetManagerData
) {
  const positions: Position[] = [];

  if (
    !accountData.address ||
    !accountData.balances ||
    !accountAssetManagerData
  ) {
    return positions;
  }

  assetManagers.forEach(assetManager => {
    const assetManagerAccount =
      accountAssetManagerData?.account?.assetManagers.find(am =>
        Object.values(assetManager.deployments).find(
          depl => am.assetManager.id.toLowerCase() === depl.toLowerCase()
        )
      );

    if (!assetManagerAccount) {
      return;
    }

    // Get chart data for the asset manager's base asset
    const chartDataItem =
      chartData[assetManager.portfolio.baseAsset.asset.toLowerCase()];

    if (!chartDataItem) {
      return;
    }

    const positionId = `${assetManager.id}-${accountData.address}`;

    // Create a record for daily history lookups (similar to getPrice in useChartData)
    const dailyHistoryRecord = assetManager.dailyHistory.reduce(
      (obj, daily) => {
        obj[daily.timestamp.toString()] = daily.shareValue;
        return obj;
      },
      {} as Record<string, string>
    );

    const tick = 24 * 60 * 60; // Daily interval in seconds

    const baseValueAt = (timestamp: number) => {
      return (
        chartData[
          assetManager.portfolio.baseAsset.asset.toLowerCase()
        ].getPrice(timestamp) || 0
      );
    };

    const getValueAt = function (
      this: PositionBalance,
      timestampInSeconds: number
    ) {
      const baseValue = baseValueAt(timestampInSeconds);

      // Convert timestamp to seconds and calculate interval-based lookup
      const interval = BigInt(tick);
      const intervalTimestamp = (
        (BigInt(timestampInSeconds) / interval) *
        interval
      ).toString();

      // Look up share value in record using interval-based timestamp
      const historyShareValue =
        parseFloat(dailyHistoryRecord[intervalTimestamp] || '0') * baseValue ||
        parseFloat(assetManager.contract.shareValueUSD);

      return (
        +formatUnits(
          this.balance,
          parseInt(assetManager.contract.decimals.toString())
        ) * historyShareValue
      );
    };

    const currentTime = Math.floor(Date.now() / 1000);
    const currentBalance: PositionBalance<AssetManagerData> = {
      value: 0,
      balance: assetManagerAccount.shares,
      timestamp: currentTime,
      meta: assetManager,
      blockNumber: '0',
      getValueAt,
    };
    const currentValue = getValueAt.call(currentBalance, currentTime);
    currentBalance.value = currentValue;

    const position: Position<AssetManagerData, any, any> = {
      id: positionId,
      type: 'AssetManager',
      symbol: assetManager.contract.symbol,
      name: `${assetManager.contract.name} - ${accountData.address.slice(
        0,
        6
      )}...${accountData.address.slice(-4)}`,
      decimals: parseInt(assetManager.contract.decimals),
      image: chartDataItem.image,
      color: chartDataItem.color,
      balances: [],
      events: [],
      growth: 0,
      chartData: JSON.parse(JSON.stringify(chartDataItem.timeSeries)),
      assets: [],
      balance: formatUnits(
        assetManagerAccount.shares,
        parseInt(assetManager.contract.decimals)
      ),
      value: currentValue,
      meta: assetManager,
    };

    position.balances.push(currentBalance);

    let remainingBalance = BigInt(currentBalance.balance);
    position.balances.push(
      ...(accountAssetManagerData.account?.transactions
        .filter(tx =>
          compareAddresses(
            tx.assetManager.id,
            assetManagerAccount.assetManager.id
          )
        )
        .sort((a, b) => b.timestamp - a.timestamp)
        .flatMap(tx => {
          const positionBalance: PositionBalance<AssetManagerData> = {
            value: 0,
            balance: remainingBalance.toString(),
            timestamp: tx.timestamp,
            meta: assetManager,
            blockNumber: '0',
            getValueAt,
          };
          remainingBalance -= BigInt(tx.shares);
          positionBalance.value = getValueAt.call(
            positionBalance,
            tx.timestamp
          );
          return [positionBalance];
        }) || [])
    );

    position.balances.push({
      value: 0,
      balance: remainingBalance.toString(),
      timestamp: chartDataItem.timeSeries[0].timestamp,
      meta: assetManager,
      blockNumber: '0',
      getValueAt,
    });

    position.balances.reverse();

    // Add assets from the asset manager
    Object.entries(assetManager.assets).forEach(([assetAddress, asset]) => {
      const assetBalance = assetManager.assetBalances[assetAddress] || '0';
      const userAllocation =
        (BigInt(assetManagerAccount.shares) * BigInt(assetBalance)) /
        BigInt(assetManager.contract.totalSupply);
      const assetValue =
        +formatUnits(userAllocation, asset.decimals) *
        (chartData[assetAddress.toLowerCase()]?.getPrice() || 0); // TODO: get actual price

      position.assets.push({
        id: assetAddress,
        symbol: asset.symbol,
        name: asset.name,
        image: asset.image.thumb || asset.image.small || asset.image.thumb,
        balance: userAllocation.toString(),
        decimals: parseInt(asset.decimals),
        color: asset.color,
        value: assetValue,
      });
    });

    // Add chart data
    try {
      addPositionChartData(position);
    } catch (e) {
      console.error('Error adding chart data for position:', position.id, e);
      return;
    }

    positions.push(position);
  });

  return positions;
}
